import { Network, Visibility, zodAddress, zodEventStatus, zodEventType, zodField, zodNetwork, zodRecordStatus, zodViewKey, zodVisibility, } from '@puzzlehq/types';
import { Types } from 'mongoose';
import { z } from 'zod';
import { RaffleV2Type } from './raffle.js';
import { countries } from 'countries-list';
import { LeaderboardDuration, LeaderboardRewardType, LeaderboardType, } from './leaderboard.js';
import { NewUserQuestType } from './quests.js';
export const zodClient = z.enum(['extension', 'ios', 'android']);
export const zodTransition = z.object({
    transitionId: z.string(),
    programId: z.string(),
    functionId: z.string(),
    inputs: z.array(z.string().nullable()),
    outputs: z.array(z.string().nullable()),
});
/// todo - update this zod type to validate record ciphertexts
// export const zodRecord = z.string();
/// todo - update this zod type to validate amounts (ie "100u64")
// export const zodAmount = z.number().positive();
export const zodEventCreate = z.object({
    type: zodEventType,
    owner: zodAddress,
    network: zodNetwork.optional().default(Network.AleoMainnet),
    description: z.string().optional(),
    transactionId: z.string().optional(),
    programId: z.string().optional(),
    functionId: z.string().optional(),
    transitions: z
        .object({
        transitionId: z.string(),
        programId: z.string(),
        functionId: z.string(),
        inputs: z.array(z.string()).optional(),
        outputs: z.array(z.string()).optional(),
    })
        .array()
        .default([]),
    visibility: zodVisibility.optional(),
    inputs: z.string().array().default([]),
    tokenIds: z.array(zodField).optional(),
    clientInfo: z
        .object({
        client: zodClient,
        version: z.string(),
        device: z.string().optional(),
    })
        .optional(),
});
/// mongo documents
export const zodMongoId = z
    .instanceof(Types.ObjectId)
    .transform((id) => id.toString())
    .or(z.string());
export const zodProvePayload = z.object({
    inputs: z.array(z.string()),
    fee: z.number().nonnegative(),
    feeRecord: z.string().optional(),
    feeRecordCiphertext: z.string().optional(),
    authorization: z.string().optional(),
    feeAuthorization: z.string().optional(),
    privateKey: z.string().optional(),
    inputRecordIds: z.array(zodMongoId).optional(),
});
export const zodAccount = z.object({
    _id: zodMongoId,
    address: zodAddress,
    viewkey: zodViewKey
        .nullish()
        .transform((value) => (value === null ? undefined : value)),
    viewer: zodAddress,
    imported: z.boolean(),
    shared: z.boolean(),
    walletAddress: z.string().optional(),
    archivedHeight: z.number().int().nullish(),
});
export const zodWallet = z.object({
    _id: zodMongoId,
    walletAddress: z.string(),
    pro: z.boolean(),
    created: z.date(),
    imported: z.boolean(),
    primaryAccount: zodAddress.optional(),
    accounts: zodAddress.array(),
    nativeAccountsLen: z.number(),
    puzzlePointsBalance: z.number().int().optional(),
    lifetimePuzzlePoints: z.number().int().optional(),
    referralCode: z.string().optional(),
    signupCode: z.string().optional(),
    discordUserId: z.string().optional(),
    discordHandle: z.string().optional(),
});
export const zodAsset = z.object({
    tokenId: z.string(),
    name: z.string(),
    symbol: z.string().optional(),
    programId: z.string(),
    decimals: z.number().int().nonnegative(),
    supply: z.number().int().nonnegative().optional(),
    isARC20: z.boolean(),
    displayIfZero: z.boolean().optional(),
    recordName: z.string(),
});
export const zodAssetV2 = z.object({
    tokenId: z.string(),
    name: z.string(),
    symbol: z.string().optional(),
    decimals: z.number().int().nonnegative(),
    supply: z.number().int().nonnegative().optional(),
    maxSupply: z.number().int().nonnegative().optional(),
    admin: zodAddress.optional(),
    externalAuthorizationRequired: z.boolean().optional(),
    externalAuthorizationParty: zodAddress.optional(),
    isMTSP: z.boolean(),
    programId: z.string(),
    recordName: z.string(),
    displayIfZero: z.boolean().optional(),
    iconURL: z.string().optional(),
    usageCount: z.number().int().nonnegative().optional(),
    priority: z.number().int().nonnegative(),
    coinbaseSymbol: z.string().optional(),
});
export const zodCoverageType = z.preprocess((val) => (val === 'all_pro' ? 'all' : val), z.enum(['pro', 'all', 'none']));
export const zodEvent = z.object({
    _id: zodMongoId,
    type: zodEventType,
    owner: zodAddress,
    status: zodEventStatus,
    created: z.date(),
    broadcast: z.date().optional(),
    settled: z.date().optional(),
    network: zodNetwork.optional().default(Network.AleoMainnet),
    transactionId: z.string().optional(),
    programId: z.string().default(''),
    functionId: z.string().default(''),
    inputs: z.string().array().default([]),
    transitions: zodTransition.array().default([]),
    height: z.number().int().nonnegative().optional(),
    description: z
        .string()
        .nullish()
        .transform((val) => (val === null ? undefined : val)),
    fee: z.number().nonnegative().default(0),
    feeCovered: z.boolean().default(false),
    feeCoverageType: zodCoverageType.optional().default('none'),
    visibility: zodVisibility.default(Visibility.Public),
    error: z.string().optional(),
    questRewardId: z.string().nullish(),
    tokenIds: z
        .array(zodField)
        .nullish()
        .transform((val) => (val === null ? undefined : val)),
    giveawayId: z.string().optional(),
    /** For receives: sender from decryptSender or txn.owner. */
    sender: z.string().optional(),
    /** When sender decryption was attempted but failed. */
    senderError: z.string().optional(),
    /** For Receive + transfer_private: output index of the received record (compliant stablecoin use 2, credits use 1). */
    receiveOutputIndex: z.number().int().nonnegative().optional(),
    /** For Receive: transition index that produced the receive (for sender resolution). */
    receiveTransitionIndex: z.number().int().nonnegative().optional(),
});
export const zodRecord = z.object({
    _id: zodMongoId,
    spentEventId: z
        .string()
        .nullish()
        .transform((val) => (val === null ? undefined : val)),
    height: z.number(),
    timestamp: z.date(),
    ciphertext: z.string(),
    programId: z.string(),
    functionId: z.string(),
    name: z.string(),
    network: zodNetwork.optional().default(Network.AleoMainnet),
    transitionId: z.string(),
    transactionId: z.string(),
    index: z.number(),
    owner: zodAddress.nullish(),
    serialNumber: z.string().nullish(),
    status: zodRecordStatus,
});
export const zodRecordWithPlaintext = zodRecord.extend({
    plaintext: z.string(),
    microcredits: z.number(),
    data: z.record(z.string(), z.any()),
});
export const zodZapp = z.object({
    _id: zodMongoId,
    title: z.string(),
    subtitle: z.string().optional(),
    body: z.string(),
    imageUrl: z.string().optional(),
    backgroundImageUrl: z.string().optional(),
    backgroundHex: z.string(),
    url: z.string(),
    featured: z.boolean().optional(), // future proofing
    category: z.string().optional(), // future proofing
});
export const zodFee = z.object({
    _id: zodMongoId,
    programId: z.string(),
    functionId: z.string(),
    fee: z.object({
        total: z.number(),
        storageFee: z.number().optional(),
        finalizeFees: z.number().array().optional(),
        priorityFee: z.number().optional(),
        burntFee: z.number().optional(),
    }),
});
export const zodStatus = z.object({
    title: z.string(),
    subtitle: z.string().optional(),
    color: z.enum(['green', 'yellow', 'red', 'blue', 'gray']),
});
export const zodSponsor = z.object({
    _id: zodMongoId,
    name: z.string(),
    url: z.string(),
    splashPageTextColor: z.enum(['dark', 'light']).default('dark'),
    arcadeCardTextColor: z.enum(['dark', 'light']).default('dark'),
    arcadeRowBackgroundImageExtensionURL: z.string().optional(),
    arcadeRowBackgroundImageMobileURL: z.string().optional(),
    splashPageBackgroundImageExtensionURL: z.string().optional(),
    splashPageBackgroundImageMobileURL: z.string().optional(),
    splashPageImageDesktopURL: z.string().optional(),
    backgroundImageExtensionURL: z.string().optional(),
    backgroundImageMobileURL: z.string().optional(),
    logoURL: z.string().optional(),
});
const zodSocials = z.object({
    youtube: z.string().optional(),
    x: z.string().optional(),
    telegram: z.string().optional(),
    discord: z.string().optional(),
});
export const zodQuest = z.object({
    _id: zodMongoId,
    title: z.string(),
    bentoTitle: z.string().optional(),
    description: z.string().optional(),
    url: z.string(),
    imageURL: z.string().optional(),
    backgroundImageURL: z.string().optional(),
    backgroundSplashImageURL: z.string().optional(),
    bentoSideImageURL: z.string().optional(),
    logoURL: z.string().optional(),
    programId: z.string(),
    functionIds: z.string().array(),
    mintAmount: z.number(),
    questType: z
        .enum(['pro', 'referral', 'onramp', 'betastaking', 'arcane', 'leaderboard'])
        .optional(),
    dailyLimit: z.number(),
    dailyProLimit: z.number(),
    limitText: z.string().optional(),
    proLimitText: z.string().optional(),
    instructionsHeader: z.string().optional(),
    instructions: z.string().array().optional(),
    action: z.string().optional(),
    socials: zodSocials.optional(),
});
export const zodQuestProgress = z.object({
    _id: zodMongoId,
    dailyEarnings: z.number().int().nonnegative(),
    dailyLimit: z.number().int().nonnegative(),
    isPro: z.boolean(),
    reset: z.date(),
});
export const zodDailyQuestProgress = z.object({
    totalDailyEarnings: z.number().int().nonnegative(),
    totalDailyLimit: z.number().int().nonnegative(),
    isPro: z.boolean(),
    reset: z.date(),
    completedAllQuests: z.boolean(),
});
export const zodNewUserQuest = z.object({
    _id: zodMongoId,
    title: z.string(),
    url: z.string().optional(),
    type: z.nativeEnum(NewUserQuestType),
    imageURL: z.string().optional(),
    inProgress: z.boolean(),
    completed: z.boolean(),
    ctaText: z.string().optional(),
});
export const zodOffer = z.object({
    referralCode: z.string(),
    details: z.string(),
    postPurchaseDetails: z.string(),
    postPurchaseTitle: z.string(),
    aleoAmount: z.number().optional(),
    parcAmount: z.number().optional(),
    partAmount: z.number().optional(),
    aleoAmountInUSD: z.number().optional(),
});
export const zodAd = z.object({
    _id: zodMongoId,
    title: z.string(),
    subtitle: z.string(),
    buttonTitle: z.string(),
    url: z.string(),
    imageURL: z.string().optional(),
    bgImageURL: z.string().optional(),
    bgColorHex: z.string().optional(),
});
export const zodRaffle = z.object({
    _id: zodMongoId,
    title: z.string(),
    draw: z.date().optional(),
    cutoff: z.date(),
    programId: z.string(),
    prizeFunctionId: z.string(),
    prizeRecordName: z.string(),
    entryFunctions: z.record(z.string()),
    entries: z.number().int().nonnegative(),
    prizeTagline: z.string(),
    prizeDescription: z.string(),
    prizeURL: z.string().optional(),
    ticketPrice: z.number().nonnegative(),
    winnerAddress: zodAddress.optional(),
    sponsor: zodSponsor,
});
export const zodRaffleV2Base = z.object({
    _id: zodMongoId,
    title: z.string(),
    giveawayId: z.string().optional(),
    draw: z.date().optional(),
    cutoff: z.date(),
    programId: z.string(),
    prizeFunctionId: z.string(),
    prizeRecordName: z.string(),
    entryFunctions: z.record(z.string()),
    entries: z.number().int().nonnegative(),
    maxEntries: z.number().int().nonnegative().optional(),
    prizeDescription: z.string(),
    prizeSumDescription: z.string().optional(),
    ticketPrice: z.number().nonnegative(),
    winnerAddresses: zodAddress.array().default([]),
    winnerCount: z.number().int(),
    sponsor: zodSponsor,
    fees: z.record(z.number()).optional(),
    type: z.nativeEnum(RaffleV2Type),
    proOnly: z.boolean().optional(),
});
export const zodClientVersion = z.object({
    latestVersion: z.string().optional(),
    minimumVersion: z.string().optional(),
});
export const zodBugReport = z.object({
    message: z.string(),
    version: z.string(),
    wallet: z.object({
        selectedAddress: z.string().optional(),
        accountsLen: z.number().int().optional(),
        network: z.string().optional(),
    }),
    session: z.object({
        walletAddress: z.string().optional(),
        selectedAddress: z.string().optional(),
        isHexSaved: z.boolean().optional(),
        ABTestGroup: z.boolean().optional(),
    }),
    local: z.object({
        imported: z.boolean().optional(),
        isSeedSaved: z.boolean().optional(),
        isHexSaved: z.boolean().optional(),
        selectedAddress: z.string().optional(),
        accountsLen: z.number().int().optional(),
        network: z.string().optional(),
    }),
});
export const zodAleoProgram = z.object({
    _id: zodMongoId,
    programId: z.string(),
    source: z.string(),
});
export const zodRafflePrizeView = z.object({
    _id: zodMongoId,
    programId: z.string(),
    giveawayId: z.string().optional(),
    winnerAddress: z.string(),
    hasViewedPrize: z.boolean(),
});
export const zodExchange = z.object({
    name: z.string(),
    url: z.string(),
    iconURL: z.string(),
});
export const zodPaywall = z.object({
    backgroundImageURL: z.string(),
    backgroundColorHex: z.string().optional(),
    textColor: z.enum(['light', 'dark']),
});
export const zodArcadeGame = z.object({
    name: z.string(),
    url: z.string(),
    imageURL: z.string(),
    parcCost: z.number().int().nonnegative(),
    programId: z.string(),
    functionIds: z.string().array(),
    fee: z.number(),
    covered: z.boolean(),
    feeCoverageType: zodCoverageType,
});
export const zodArcadeStoreOrder = z.object({
    address: zodAddress,
    price: z.number().int().positive(),
    skuId: z.number().int(),
    skuTitle: z.string(),
    smallLogoImageURL: z.string().url().optional(),
    purchased: z.date(),
    purchaseStatus: zodEventStatus,
    purchaseError: z.string().optional(),
    fulfilled: z.date().optional(),
    fulfillmentStatus: zodEventStatus.optional(),
    fulfillmentError: z.string().optional(),
});
export const zodStoreService = z.object({
    _id: zodMongoId.optional(),
    title: z.string(),
    description: z.string().optional(),
    url: z.string().url(),
    slug: z.string(),
    availability: z.string().optional(),
    redemptionInstructions: z.string().optional(),
    redeemURL: z.string().url().optional(),
    termsAndConditions: z.string().optional(),
    codeLength: z.number().int().positive().optional(),
    priceRange: z.string(),
    bigLogoImageURL: z.string(),
    smallLogoImageURL: z.string().optional(),
    previewImageURLs: z.string().array().optional(),
    display: z.boolean(),
    sponsor: zodSponsor.optional(),
    countries: z.string().array().optional(),
    availableInUserCountry: z.boolean().optional(),
});
export const zodProductSKU = z.object({
    _id: zodMongoId,
    skuId: z.number().int(),
    title: z.string(),
    service: zodStoreService,
    url: z.string().url().optional(),
    display: z.boolean(),
    price: z.number().int().positive(),
    stock: z.number().int().nonnegative().catch(0),
    programId: z.string(),
    proOnly: z.boolean(),
    // optional overrides
    availability: z.string().optional(),
    countries: z.string().array().optional(),
    redemptionInstructions: z.string().optional(),
    redeemURL: z.string().url().optional(),
    termsAndConditions: z.string().optional(),
    codeLength: z.number().int().positive().optional(),
    bigImageURL: z.string().url().optional(),
    smallImageURL: z.string().url().optional(),
    availableInUserCountry: z.boolean().optional(),
});
export const zodStoreChainData = z.object({
    inventoryProgramId: z.string(),
    programFunctionIds: z.record(z.object({
        programId: z.string(),
        functionId: z.string(),
    })),
});
export const zodProductSKUWithChainData = zodProductSKU.extend({
    chainData: zodStoreChainData.optional(),
});
const zodProductSKUWithoutSponsorInService = zodProductSKU.extend({
    service: zodStoreService.omit({ sponsor: true }),
});
const zodRaffleV2Eth = zodRaffleV2Base.extend({
    type: z.literal(RaffleV2Type.eth).optional().default(RaffleV2Type.eth),
    productSku: zodProductSKUWithoutSponsorInService,
    prizeURLs: z.string().array(),
});
const zodRaffleV2Product = zodRaffleV2Base.extend({
    type: z.literal(RaffleV2Type.product),
    productSku: zodProductSKUWithoutSponsorInService,
});
const zodRaffleV2Aleo = zodRaffleV2Base.extend({
    type: z.literal(RaffleV2Type.aleo),
});
export const zodRaffleV2 = z.union([
    zodRaffleV2Eth,
    zodRaffleV2Product,
    zodRaffleV2Aleo,
]);
export const zodArcadeStoreUserProduct = z.object({
    _id: zodMongoId,
    code: z.string(),
    used: z.boolean().optional().default(false),
    productSKU: zodProductSKU,
    giveawayId: z.string().optional(),
});
export const zodArcadeStoreCountryCode = z
    .string()
    .refine((val) => val === 'global' || Object.keys(countries).includes(val), { message: 'Invalid country code' });
export const zodLinkDiscordResponse = z.object({
    discordHandle: z.string(),
    isInPuzzleWalletDiscord: z.boolean(),
    rewardDate: z.date().optional(),
    joinDate: z.date().optional(),
    status: z.string().optional(),
});
export const zodGetDiscordResponse = z.object({
    discordHandle: z.string(),
    isInPuzzleWalletDiscord: z.boolean(),
    rewardDate: z.date().optional(),
    joinDate: z.date().optional(),
});
export const zodLeaderboardEntry = z.object({
    primaryAccount: z.string(),
    totalAmount: z.number().int(),
    rank: z.number().int(),
});
export const zodLeaderboardReward = z.object({
    type: z.nativeEnum(LeaderboardRewardType),
    rewards: z.number().nonnegative().array(),
});
export const zodLeaderboard = z.object({
    duration: z.nativeEnum(LeaderboardDuration),
    type: z.nativeEnum(LeaderboardType),
    startDate: z.date(),
    lastProcessedAt: z.date(),
    entries: z.array(zodLeaderboardEntry),
    reward: zodLeaderboardReward.optional(),
});
export const zodLeaderboardWithUserInfo = zodLeaderboard.extend({
    userEntry: z
        .object({
        primaryAccount: z.string(),
        totalAmount: z.number().int(),
        rank: z.number().int(),
    })
        .optional(),
});
export const zodUserStreak = z.object({
    _id: zodMongoId,
    programId: z.string(),
    functionId: z.string(),
    fee: z.object({
        total: z.number(),
        storageFee: z.number().optional(),
        finalizeFees: z.number().array().optional(),
        priorityFee: z.number().optional(),
        burntFee: z.number().optional(),
    }),
});
export const zodStreakMilestone = z.object({
    daysRequired: z.number().int().positive(),
    baseReward: z.number().int().nonnegative(),
    proBonus: z.number().int().nonnegative(),
});
export const zodStreakData = z.object({
    currentStreak: z.number().int().nonnegative(),
    canClaimReward: z.boolean(),
    milestones: z.array(zodStreakMilestone),
});
export const zodDailyStatsData = z.object({
    parcEarned: z.number().int().nonnegative(),
    partEarned: z.number().int().nonnegative(),
    giveawaysEntered: z.number().int().nonnegative(),
    gamesPlayed: z.number().int().nonnegative(),
});
export const zodFriend = z.object({
    walletAddress: z.string(),
    friendWalletAddress: z.string(),
    createdAt: z.date(),
    friendReferralCode: z.string().optional(),
    friendEthAddress: z.string().optional(),
    status: z.enum(['friend', 'pending', 'sent']),
});
export const zodFriendGamePerformance = zodFriend.extend({
    part: z.number().int().nonnegative().nullable(),
});
export const zodCreateSponsorInput = z.object({
    network: zodNetwork.optional().default(Network.AleoMainnet),
    name: z.string(),
    url: z.string().url(),
    arcadeRowBackgroundImageExtensionURL: z.string().url().optional(),
    arcadeRowBackgroundImageMobileURL: z.string().url().optional(),
    splashPageBackgroundImageExtensionURL: z.string().url().optional(),
    splashPageBackgroundImageMobileURL: z.string().url().optional(),
    logoURL: z.string().url().optional(),
    password: z.string(),
});
export const zodCreateServiceInput = z.object({
    network: zodNetwork.optional().default(Network.AleoMainnet),
    title: z.string(),
    description: z.string().optional(),
    url: z.string().url(),
    slug: z.string(),
    availability: z.string().optional(),
    countries: zodArcadeStoreCountryCode.array().optional(),
    redemptionInstructions: z.string().optional(),
    redeemURL: z.string().optional(),
    termsAndConditions: z.string().optional(),
    codeLength: z.number().int().positive(),
    priceRange: z.string(),
    bigLogoImageURL: z.string(),
    smallLogoImageURL: z.string(),
    previewImageURLs: z.string().array().optional(),
    sponsorId: z.string(),
    password: z.string(),
});
export const zodCreateProductSKUInput = z.object({
    network: zodNetwork.optional().default(Network.AleoMainnet),
    skuId: z.number().int(),
    title: z.string(),
    sponsorId: z.string(),
    serviceId: z.string(),
    url: z.string().optional(),
    display: z.boolean(),
    price: z.number().int().nonnegative(),
    programId: z.string(),
    proOnly: z.boolean(),
    availability: z.string().optional(),
    countries: zodArcadeStoreCountryCode.array().optional(),
    redemptionInstructions: z.string().optional(),
    redeemURL: z.string().optional(),
    termsAndConditions: z.string().optional(),
    codeLength: z.number().int().optional(),
    bigImageURL: z.string().url().optional(),
    smallImageURL: z.string().url().optional(),
    password: z.string(),
});
